import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [logoAnimated, setLogoAnimated] = useState(false);
  const location = useLocation();
  const logoRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLogoAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 shadow-md'
      }`}
    >
      {/* Tagline Bar */}
      <div className="bg-[#06B6D4] text-white text-center py-1.5 px-4">
        <span className="text-xs font-semibold tracking-[0.2em] uppercase">✦ Magnify Your Brand ✦</span>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="relative flex items-center hover:scale-105 transition-transform duration-300 group">
            {/* Glow effect on hover */}
            <div className="absolute inset-0 -m-2 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                 style={{
                   background: 'radial-gradient(ellipse at center, rgba(6, 182, 212, 0.4) 0%, transparent 70%)',
                   filter: 'blur(8px)',
                 }}
            />
            {/* Shimmer overlay */}
            <div className="absolute inset-0 -m-1 overflow-hidden rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"
                   style={{
                     background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
                   }}
              />
            </div>
            <img
              ref={logoRef}
              src="https://static.readdy.ai/image/1a26ac5983f572a98909fa1cfb0c220a/9efa73a4de54dc9e9bbf665ad1b8c213.png"
              alt="CZ Creative Media"
              className="h-20 w-auto relative z-10"
              style={{
                opacity: logoAnimated ? 1 : 0,
                transform: logoAnimated ? 'scale(1) translateY(0px)' : 'scale(0.6) translateY(-12px)',
                transition: 'opacity 0.7s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)',
              }}
            />
          </Link>

          {/* Center Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-all duration-200 relative group ${
                isActive('/') ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Home
              <span
                className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#06B6D4] transform origin-left transition-transform duration-200 ${
                  isActive('/') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}
              ></span>
            </Link>
            <Link
              to="/about"
              className={`text-sm font-medium transition-all duration-200 relative group ${
                isActive('/about') ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              About
              <span
                className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#06B6D4] transform origin-left transition-transform duration-200 ${
                  isActive('/about') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}
              ></span>
            </Link>
            <Link
              to="/services"
              className={`text-sm font-medium transition-all duration-200 relative group ${
                isActive('/services') ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Services
              <span
                className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#06B6D4] transform origin-left transition-transform duration-200 ${
                  isActive('/services') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}
              ></span>
            </Link>
            <Link
              to="/portfolio"
              className={`text-sm font-medium transition-all duration-200 relative group ${
                isActive('/portfolio') ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Project Gallery
              <span
                className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#06B6D4] transform origin-left transition-transform duration-200 ${
                  isActive('/portfolio') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}
              ></span>
            </Link>
          </div>

          {/* Right Side - Contact Button */}
          <div className="flex items-center space-x-4">
            <a href="tel:+18562132918" className="hidden lg:flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors">
              <i className="ri-phone-line mr-2"></i>
              856-213-2918
            </a>
            <Link
              to="/contact"
              className="bg-[#06B6D4] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#0F9A8A] transition-all duration-200 hover:shadow-lg whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}